export const CAMPAIGN_ATTRIBUTION_STORAGE_KEY = 'glyyd.campaignAttribution.v1';

const STORAGE_VERSION = 1;
const MAX_FIELD_LENGTH = 120;

const FIELD_ALIASES = {
  utm_source: ['utm_source'],
  utm_medium: ['utm_medium'],
  utm_campaign: ['utm_campaign'],
  journey_persona: ['journey_persona', 'persona'],
  club___program: ['club___program', 'club_program', 'program'],
  telemetry_system: ['telemetry_system', 'telemetry_systems', 'system', 'systems'],
} as const;

const TELEMETRY_SYSTEM_INTERNAL_VALUES: Record<string, string> = {
  peach: 'Peach Systems',
  peachsystems: 'Peach Systems',
  peachsystem: 'Peach Systems',
  powerline: 'Peach Systems',
  'peach systems': 'Peach Systems',
  rowhero: 'RowHero',
  'row hero': 'RowHero',
  xboat: 'xBoat',
  xboats: 'xBoat',
  x_boat: 'xBoat',
};

function removeControlCharacters(value: string): string {
  return Array.from(value)
    .filter((character) => {
      const code = character.charCodeAt(0);
      return code >= 32 && code !== 127;
    })
    .join('');
}

export interface CampaignAttributionFields {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  journey_persona?: string;
  club___program?: string;
  telemetry_system?: string;
}

export interface CampaignAttributionTouch extends CampaignAttributionFields {
  capturedAt: string;
  path?: string;
}

export interface CampaignAttributionPayload {
  version: typeof STORAGE_VERSION;
  firstTouch: CampaignAttributionTouch;
  latestTouch: CampaignAttributionTouch;
}

export interface CampaignAttributionEventContext extends CampaignAttributionFields {
  hasCampaignAttribution: boolean;
}

type StorageLike = Pick<Storage, 'getItem' | 'setItem'>;

function sanitizeCampaignValue(value: string | null): string | undefined {
  if (!value) {
    return undefined;
  }

  const sanitized = removeControlCharacters(value.replace(/<[^>]*>/g, ''))
    .trim()
    .slice(0, MAX_FIELD_LENGTH);

  return sanitized || undefined;
}

function readFirstAllowedValue(
  params: URLSearchParams,
  aliases: readonly string[]
): string | undefined {
  for (const alias of aliases) {
    const value = sanitizeCampaignValue(params.get(alias));
    if (value) {
      return value;
    }
  }

  return undefined;
}

function normalizeTelemetrySystem(value: string | undefined): string | undefined {
  if (!value) {
    return undefined;
  }

  const systems = value
    .split(/[;,|]/)
    .map((system) => system.trim())
    .filter(Boolean)
    .map((system) => {
      const normalizedKey = system
        .toLowerCase()
        .replace(/[^a-z0-9 ]/g, '')
        .replace(/\s+/g, ' ');
      return TELEMETRY_SYSTEM_INTERNAL_VALUES[normalizedKey] || undefined;
    })
    .filter((system): system is string => Boolean(system));

  const uniqueSystems = Array.from(new Set(systems));
  return uniqueSystems.length > 0 ? uniqueSystems.join(';') : undefined;
}

export function extractCampaignAttribution(search: string): CampaignAttributionFields {
  const params = new URLSearchParams(search);
  const fields: CampaignAttributionFields = {};

  const utmSource = readFirstAllowedValue(params, FIELD_ALIASES.utm_source);
  if (utmSource) fields.utm_source = utmSource;

  const utmMedium = readFirstAllowedValue(params, FIELD_ALIASES.utm_medium);
  if (utmMedium) fields.utm_medium = utmMedium;

  const utmCampaign = readFirstAllowedValue(params, FIELD_ALIASES.utm_campaign);
  if (utmCampaign) fields.utm_campaign = utmCampaign;

  const journeyPersona = readFirstAllowedValue(params, FIELD_ALIASES.journey_persona);
  if (journeyPersona) fields.journey_persona = journeyPersona;

  const clubProgram = readFirstAllowedValue(params, FIELD_ALIASES.club___program);
  if (clubProgram) fields.club___program = clubProgram;

  const telemetrySystem = normalizeTelemetrySystem(
    readFirstAllowedValue(params, FIELD_ALIASES.telemetry_system)
  );
  if (telemetrySystem) fields.telemetry_system = telemetrySystem;

  return fields;
}

function hasAttributionFields(fields: CampaignAttributionFields): boolean {
  return Object.keys(fields).length > 0;
}

function readStoredAttribution(storage: StorageLike): CampaignAttributionPayload | null {
  try {
    const raw = storage.getItem(CAMPAIGN_ATTRIBUTION_STORAGE_KEY);
    if (!raw) {
      return null;
    }

    const parsed = JSON.parse(raw) as CampaignAttributionPayload;
    if (parsed?.version !== STORAGE_VERSION || !parsed.firstTouch || !parsed.latestTouch) {
      return null;
    }

    return parsed;
  } catch {
    return null;
  }
}

export function captureCampaignAttribution(
  search: string,
  storage: StorageLike = window.localStorage,
  path?: string
): CampaignAttributionPayload | null {
  const fields = extractCampaignAttribution(search);
  const existing = readStoredAttribution(storage);

  if (!hasAttributionFields(fields)) {
    return existing;
  }

  const touch: CampaignAttributionTouch = {
    ...fields,
    capturedAt: new Date().toISOString(),
    ...(path ? { path } : {}),
  };

  const payload: CampaignAttributionPayload = {
    version: STORAGE_VERSION,
    firstTouch: existing?.firstTouch || touch,
    latestTouch: touch,
  };

  try {
    storage.setItem(CAMPAIGN_ATTRIBUTION_STORAGE_KEY, JSON.stringify(payload));
  } catch {
    return existing;
  }

  return payload;
}

export function captureCampaignAttributionFromLocation(): CampaignAttributionPayload | null {
  if (typeof window === 'undefined') {
    return null;
  }

  return captureCampaignAttribution(
    window.location.search,
    window.localStorage,
    window.location.pathname
  );
}

export function getStoredCampaignAttribution(
  storage: StorageLike = window.localStorage
): CampaignAttributionPayload | null {
  return readStoredAttribution(storage);
}

export function getCampaignAttributionForHubSpot(
  storage: StorageLike = window.localStorage
): CampaignAttributionFields {
  const stored = getStoredCampaignAttribution(storage);
  if (!stored) {
    return {};
  }

  const { capturedAt: _capturedAt, path: _path, ...fields } = stored.firstTouch;
  return fields;
}

/**
 * Signup analytics properties: first-touch source/campaign (same definition as HubSpot) plus the
 * latest touch, so a returning visitor's newer campaign (e.g. a national poster scan after
 * SW Regionals) stays attributable without changing first touch.
 */
export function getCampaignAttributionForSignupEvent(
  storage: StorageLike = window.localStorage
): Record<string, string> {
  const stored = getStoredCampaignAttribution(storage);
  if (!stored) {
    return {};
  }

  const properties: Record<string, string> = {};
  const { firstTouch, latestTouch } = stored;
  if (firstTouch.utm_source) properties.utm_source = firstTouch.utm_source;
  if (firstTouch.utm_campaign) properties.utm_campaign = firstTouch.utm_campaign;
  if (latestTouch.utm_source) properties.latest_utm_source = latestTouch.utm_source;
  if (latestTouch.utm_campaign) properties.latest_utm_campaign = latestTouch.utm_campaign;
  return properties;
}

export function getCampaignAttributionEventContext(
  storage: StorageLike = window.localStorage
): CampaignAttributionEventContext {
  const fields = getCampaignAttributionForHubSpot(storage);

  return {
    hasCampaignAttribution: hasAttributionFields(fields),
    ...fields,
  };
}
